import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Keys start with 'integrations': the server invalidates ['integrations'] after every sync and
// the stream's 'sync' events refetch it, so status cards and SyncBadges update live.
export const integrationKeys = {
  all: ['integrations'] as const,
  status: () => ['integrations', 'status'] as const,
  runs: (provider: string, teamId: string | null) => ['integrations', 'runs', provider, teamId ?? 'all'] as const,
  links: (provider: string, done: boolean) => ['integrations', 'links', provider, done] as const,
}

export const statusQuery = () =>
  queryOptions({
    queryKey: integrationKeys.status(),
    queryFn: () => unwrap(api.integrations.$get()),
    // The stream refreshes on sync events; a poll covers a dropped connection mid-run. A waiting
    // run (no worker yet, retry backoff) is not polled: its start arrives as a sync event.
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.state === 'running') ? 8_000 : false),
  })

export const runsQuery = (provider: Provider, teamId: string | null) =>
  queryOptions({
    queryKey: integrationKeys.runs(provider, teamId),
    queryFn: () => unwrap(api.integrations[':provider'].runs.$get({ param: { provider }, query: { limit: '20', ...(teamId ? { teamId } : {}) } })),
  })

export const LINK_PROVIDERS = ['dfbnet_player', 'dfbnet_official', 'fussball_de_player', 'fupa_player', 'transfermarkt_player', 'presli_member', 'veo_player'] as const
export type LinkProvider = (typeof LINK_PROVIDERS)[number]

export const linksQuery = (provider: LinkProvider | 'all', done: boolean) =>
  queryOptions({
    queryKey: integrationKeys.links(provider, done),
    queryFn: () =>
      unwrap(
        api.integrations.links.$get({
          query: { ...(provider === 'all' ? {} : { provider }), unconfirmed: done ? 'false' : 'true' },
        }),
      ),
    placeholderData: (prev) => prev,
  })

export type Status = Awaited<ReturnType<NonNullable<ReturnType<typeof statusQuery>['queryFn']>>>
export type Source = Status['items'][number]
export type Provider = Source['provider']
export type Runs = Awaited<ReturnType<NonNullable<ReturnType<typeof runsQuery>['queryFn']>>>
export type Run = Runs['items'][number]
export type Links = Awaited<ReturnType<NonNullable<ReturnType<typeof linksQuery>['queryFn']>>>
export type Link = Links['items'][number]
export type LinkPerson = Links['people'][number]

function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: integrationKeys.all })
}

export type SaveInput = {
  teamId?: string | null
  enabled?: boolean
  credentials?: Record<string, string>
  config?: Record<string, string>
}

export function useSaveSource(provider: Provider) {
  const done = useInvalidate()
  return useMutation({
    mutationFn: (json: SaveInput) => unwrap(api.integrations[':provider'].$put({ param: { provider }, json })),
    onSuccess: done,
    // Field errors are shown in the dialog; no generic toast.
    onError: () => {},
  })
}

export function useTestSource(provider: Provider) {
  return useMutation({
    mutationFn: (json: Omit<SaveInput, 'enabled'>) => unwrap(api.integrations[':provider'].test.$post({ param: { provider }, json })),
    onError: () => {},
  })
}

export function useRunSource() {
  const done = useInvalidate()
  return useMutation({
    mutationFn: (v: { provider: Provider; teamId: string | null }) =>
      unwrap(api.integrations[':provider'].run.$post({ param: { provider: v.provider }, json: { teamId: v.teamId } })),
    onSuccess: done,
  })
}

export function useLinkActions() {
  const qc = useQueryClient()
  // Links change matches, statistics and attendance; the server invalidates those too.
  const done = () => qc.invalidateQueries({ queryKey: integrationKeys.all })
  return {
    confirm: useMutation({ mutationFn: (id: string) => unwrap(api.integrations.links[':id'].confirm.$post({ param: { id } })), onSuccess: done }),
    assign: useMutation({
      mutationFn: (v: { id: string; personId: string }) => unwrap(api.integrations.links[':id'].assign.$post({ param: { id: v.id }, json: { personId: v.personId } })),
      onSuccess: done,
    }),
    unlink: useMutation({
      mutationFn: (v: { id: string; keepSuggestion?: boolean }) =>
        unwrap(api.integrations.links[':id'].unlink.$post({ param: { id: v.id }, json: { keepSuggestion: v.keepSuggestion ?? false } })),
      onSuccess: done,
    }),
    confirmSafe: useMutation({
      mutationFn: (provider?: LinkProvider) => unwrap(api.integrations.links['confirm-safe'].$post({ json: { minScore: 90, ...(provider ? { provider } : {}) } })),
      onSuccess: done,
    }),
  }
}

export type WebsitePreview = Awaited<ReturnType<typeof previewWebsite>>
export type WebsiteSuggestion = WebsitePreview['suggestions'][number]
export type OrgPatch = Parameters<typeof api.org.$patch>[0]['json']

const previewWebsite = (url: string) => unwrap(api.integrations.website.preview.$post({ json: url ? { url } : {} }))

/** Read the club website; nothing is stored until the club applies values. */
export function useWebsitePreview() {
  return useMutation({ mutationFn: previewWebsite, onError: () => {} })
}

/** Chosen values go through the club settings endpoints (PATCH /org, logo upload). */
export function useApplyWebsite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ values, logo }: { values: OrgPatch; logo: WebsitePreview['logo'] }) => {
      if (Object.keys(values).length) await unwrap(api.org.$patch({ json: values }))
      if (logo) {
        const blob = await (await fetch(logo.dataUrl)).blob()
        const ext = logo.mime === 'image/svg+xml' ? 'svg' : logo.mime.split('/')[1]
        await unwrap(api.org.logo.$post({ form: { file: new File([blob], `wappen.${ext}`, { type: logo.mime }) } }))
      }
    },
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['org'] }), qc.invalidateQueries({ queryKey: ['session'] })]),
    onError: () => {},
  })
}
